import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, of, throwError } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
} from 'rxjs/operators';
import {
  MyOrdersStoreService,
  Order,
  OrderHeader,
  OrderItem,
  orderSelectors,
} from '@usf/ngrx-order';
import {
  recentPurchaseAdapter,
  selectRecentPurchaseState,
} from '@usf/ngrx-list';
import { RecentlyPurchasedService } from '@app/lists/pages/recently-purchased/services/recently-purchased.service';
import { selectDYFViewModel } from '@order/selectors/dyf/dyf.selectors';
import { ProductService } from '@shared/services/product/product.service';
import { OrderService } from './order.service';
import { MessageStoreService } from '@app/ngrx-message/services/message/message-store.service';
import { v4 as uuidv4 } from 'uuid';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { Message } from '@app/ngrx-message/models/client/message';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { ProductPropertiesEnum } from '@usf/product-types';
import { ORDER_ACTIONS } from '@order/actions/order-actions';
import { DownloadOrderInsightsEvent } from '@order/models/download-orders-event';
import {
  OrderInsightData,
  OrderInsightsType,
} from '@shared/models/order-insights.model';
import { Product } from '@shared/models/product.model';

@Injectable({
  providedIn: 'root',
})
export class OrderInsightsService {
  constructor(
    private store: Store,
    private productService: ProductService,
    private orderService: OrderService,
    private recentlyPurchasedService: RecentlyPurchasedService,
    private myOrdersStoreService: MyOrdersStoreService,
    private messageStoreService: MessageStoreService,
    private loadingSpinnerService: LoadingSpinnerService,
  ) {}

  orderItemsNotOnRecentlyPurchased$(
    orderItems: OrderItem[],
  ): Observable<number[]> {
    return combineLatest([
      this.store.select(
        recentPurchaseAdapter.getSelectors(selectRecentPurchaseState)
          .selectEntities,
      ),
      this.recentlyPurchasedService.recentlyPurchasedProductsLoaded$(),
    ]).pipe(
      take(1),
      map(([recentlyPurchasedState, rpLoaded]) => {
        if (!rpLoaded) {
          return [];
        }
        const newProductNumbers: number[] = [];
        orderItems
          .filter(oi => !oi?.specialVendor)
          .forEach(orderItem => {
            const orderItemNum = orderItem.productNumber;
            if (!recentlyPurchasedState[orderItemNum]) {
              newProductNumbers.push(orderItemNum);
            }
          });
        return newProductNumbers;
      }),
    );
  }

  //DyfVm already filters out orderItems in state, so only pass in orderItems when cart state is empty (i.e. exceptions flow)
  dyfProductsNotOrdered$(orderItems?: OrderItem[]): Observable<number[]> {
    return this.store.select(selectDYFViewModel()).pipe(
      take(1),
      map(dyfVm => {
        if (!orderItems) {
          return (
            dyfVm?.products
              ?.filter(dyfProduct => !this.isSpecialVendor(dyfProduct?.product))
              .map(dyfProduct => dyfProduct?.product?.productNumber) ?? []
          );
        } else {
          const orderItemNumbers = new Set(
            orderItems.map(orderItem => orderItem?.productNumber),
          );
          const dyfProductsNotOrdered =
            dyfVm?.products
              ?.filter(
                dyfProduct =>
                  !this.isSpecialVendor(dyfProduct?.product) &&
                  !orderItemNumbers.has(dyfProduct?.product?.productNumber),
              )
              .map(dyfProduct => dyfProduct?.product?.productNumber) ?? [];

          return dyfProductsNotOrdered;
        }
      }),
    );
  }

  getLastOrderedDates$(
    productNumbers: number[],
  ): Observable<{ [key: number]: string | Date }> {
    return combineLatest([
      this.store.select(
        recentPurchaseAdapter.getSelectors(selectRecentPurchaseState)
          .selectEntities,
      ),
      this.recentlyPurchasedService.recentlyPurchasedProductsLoaded$(),
    ]).pipe(
      filter(([rpState, rpLoaded]) => !!rpLoaded),
      take(1),
      map(([recentlyPurchasedState, l]) => {
        let lastOrdered = {};
        productNumbers.forEach(
          (pn: number) =>
            (lastOrdered[pn] = recentlyPurchasedState[pn]?.purchaseDtm1),
        );
        return lastOrdered;
      }),
    );
  }

  loadOrderAndProductData$(orderId: string): Observable<any> {
    return this.isCustomerOrdersLoaded().pipe(
      filter(loaded => loaded),
      take(1),
      switchMap(l => {
        return this.myOrdersStoreService.getOrderWithId(orderId).pipe(
          take(1),
          switchMap(order => {
            if (!order?.orderHeader) {
              return throwError(() => new Error());
            }
            // Get all product numbers including order items to load
            const insightsProducts = this.getAllInsightsProducts(
              order.orderHeader,
            );
            const orderItemProductNumbers =
              order.orderItems?.map(item => item.productNumber) || [];
            const allInsightsProducts = Array.from(
              new Set([...insightsProducts, ...orderItemProductNumbers]),
            );
            this.productService.loadProducts(allInsightsProducts);

            return combineLatest([
              of(order),
              of(allInsightsProducts),
              this.orderService.checkProductsLoaded(allInsightsProducts),
            ]).pipe(
              filter(([o, p, productsLoaded]) => !!productsLoaded),
              take(1),
              mergeMap(([order, allProductNumbers]) =>
                combineLatest([
                  of(order),
                  this.productService.getProducts(allProductNumbers),
                  this.getLastOrderedDates$(allProductNumbers),
                ]).pipe(take(1)),
              ),
            );
          }),
        );
      }),
      catchError(_error => {
        return throwError(() => this.handleError());
      }),
    );
  }

  shouldIncludeInsightsOnOrderSubmission(orderHeader: OrderHeader): boolean {
    return (
      orderHeader.orderType === 'RT' &&
      ((!!orderHeader?.addOrderSource &&
        orderHeader?.addOrderSource === 'MO') ||
        (!!orderHeader?.updateOrderSource &&
          orderHeader?.updateOrderSource === 'MO'))
    );
  }

  downloadOrderInsights(fileInfo: DownloadOrderInsightsEvent) {
    this.store.dispatch(ORDER_ACTIONS.downloadOrderInsights(fileInfo));
  }
  filterSubbedOrReplacedOrderItems(orderItems: OrderItem[]): OrderItem[] {
    const removedProducts = new Set();
    for (let item of orderItems) {
      if (
        !!item?.substituteFor &&
        (item?.substituteFlag === 'R' || item?.substituteFlag === 'S')
      ) {
        removedProducts.add(item.substituteFor);
      }
    }
    return orderItems.filter(item => !removedProducts.has(item.productNumber));
  }

  isCustomerOrdersLoaded(): Observable<boolean> {
    return this.store.select(orderSelectors.selectCustomerOrdersLoaded());
  }

  loadProducts(productNumbers: number[]): void {
    this.productService.loadProducts(productNumbers);
  }

  getAllInsightsProducts(orderHeader: OrderHeader): number[] {
    return (orderHeader?.dyfProductsNotOrdered || []).concat(
      orderHeader?.newProductsOrderedNotInRecentlyPurchased || [],
    );
  }

  handleError() {
    this.loadingSpinnerService.dismissSpinnerModal();
    this.messageStoreService.upsertMessage({
      id: this.uuid(),
      stack: null,
      watermark: new Date().toUTCString(),
      read: false,
      type: MessageTypeEnum.error,
      display:
        "We're sorry but the insights for this order are not available at this time. Please try again later.",
      toast: false,
      sticky: false,
      dismissRoute: ['my-orders-view'],
    } as Message);
  }

  isSpecialVendor(product: Product): boolean {
    return (
      !!product?.inventory?.productStatus &&
      !(
        ['0', '2', '3', '4', '8', '9'].includes(
          product?.inventory?.productStatus,
        ) ||
        (product?.inventory?.productStatus === '1' &&
          !product?.summary?.properties?.has(
            ProductPropertiesEnum.specialVendor,
          ))
      )
    );
  }
  getOrderedQuantityString(
    orderItems: OrderItem[],
    productNumber: number,
  ): string {
    const item = orderItems.find(oi => oi.productNumber === productNumber);
    let orderedStr = '';
    if (item?.unitsOrdered?.currentValue) {
      orderedStr += `${item?.unitsOrdered?.currentValue} CS`;
    }
    if (item?.unitsOrdered?.currentValue && item?.eachesOrdered?.currentValue) {
      orderedStr += ' / ';
    }
    if (item?.eachesOrdered?.currentValue) {
      orderedStr += `${item?.eachesOrdered?.currentValue} EA`;
    }
    return orderedStr || '-';
  }

  getOrderInsightDataMap(
    order: Order,
    productMap: Map<number, Product>,
    lastOrderedDates: {
      [key: number]: string | Date;
    },
  ): Map<number, OrderInsightData> {
    const insightsInformation: Map<number, OrderInsightData> = new Map();
    for (let productNumber of order?.orderHeader?.dyfProductsNotOrdered) {
      const orderInsightsData: OrderInsightData = {
        orderInsightType: OrderInsightsType.LostLines,
        product: productMap.get(productNumber),
        lastOrderedDate: lastOrderedDates[productNumber],
      };
      insightsInformation.set(productNumber, orderInsightsData);
    }
    for (let productNumber of order?.orderHeader
      ?.newProductsOrderedNotInRecentlyPurchased) {
      const orderInsightsData: OrderInsightData = {
        orderInsightType: OrderInsightsType.NewProductsOrdered,
        product: productMap.get(productNumber),
        casesAndEachesOrdered: this.getOrderedQuantityString(
          order?.orderItems,
          productNumber,
        ),
      };
      insightsInformation.set(productNumber, orderInsightsData);
    }
    return insightsInformation;
  }

  uuid() {
    return uuidv4();
  }
}
